import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  VStack,
  Button,
  useToast,
  HStack,
  Grid,
  GridItem,
  Divider,
  Box,
  Text, Wrap, WrapItem, Tag, TagLabel, TagCloseButton
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useCreateClient, useUpdateClient } from '../../hooks/useClients';
import { Project } from '../../api/projectApi'; 
import { useProjects } from '../../hooks/useProjects';
import { Select } from '@chakra-ui/react';

interface ClientFormProps {
  formData: {
    clientName: string;
    additionalInformation: {
      leadPersonName: string;
      phone: string;
      email: string;
      secondaryContact?: string;
      websiteUrl?: string;
    };
    project?: string[];
  };
  setFormData: (data: ClientFormProps['formData']) => void;
  onClose?: () => void; // optional, for closing modal after save
  isEdit?: boolean; // true if editing, false if creating
  editingClient?: { _id: string } | null;
}

const ClientForm: React.FC<ClientFormProps> = ({
  formData,
  setFormData,
  onClose,
  isEdit = false,
  editingClient,
}) => {
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const toast = useToast();
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);

  const handleToggleAdditional = () => setShowAdditional((prev) => !prev);

  const handleSave = async () => {
    // Basic validation
    if (!formData.clientName.trim()) {
      toast({
        title: 'Error',
        description: 'Client name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEdit && editingClient && editingClient._id) {
        await updateClientMutation.mutateAsync({
          id: editingClient._id,
          data: formData,
        });
        toast({
          title: 'Success',
          description: 'Client updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createClientMutation.mutateAsync(formData);
        toast({
          title: 'Success',
          description: 'Client created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        if (setFormData) {
          setFormData({
            clientName: '',
            additionalInformation: {
              leadPersonName: '',
              phone: '',
              email: '',
              secondaryContact: '',
              websiteUrl: '',
            },
          });
        }
      }
      if (onClose) onClose();
    } catch (error: any) {
      // Handle 409 Conflict (duplicate client)
      if (error?.response?.status === 409) {
        if (isEdit) {
          toast({
            title: 'Duplicate Client Name',
            description: 'Another client with this name already exists. Please choose a different name.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Duplicate Client',
            description: 'A client with this name already exists. Please choose a different name.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Error',
          description: error?.message || 'Something went wrong',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Basic Information Section */}
      <Box>
        <FormControl mb={4}>
          <FormLabel fontWeight="semibold" color="blue.700">Client Name *</FormLabel>
          <Input
            value={formData.clientName}
            onChange={e => setFormData({ ...formData, clientName: e.target.value })}
            placeholder="Enter client name"
            size="lg"
            focusBorderColor="blue.400"
            borderWidth="2px"
          />
        </FormControl>

        <FormControl isDisabled={projectsLoading}>
          <FormLabel fontWeight="semibold" color="blue.700">Attach Projects</FormLabel>
          {/* Selected Projects Display */}
          {formData.project && formData.project.length > 0 && (
            <Box mb={3}>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Selected Projects:
              </Text>
              <Wrap spacing={2}>
                {formData.project.map((projId, index) => {
                  const proj = projects?.find((p: Project) => p._id === projId);
                  return proj ? (
                    <WrapItem key={projId}>
                      <Tag size="md" colorScheme="blue" variant="solid">
                        <TagLabel>{proj.projectName} ({proj.projectNumber})</TagLabel>
                        <TagCloseButton onClick={() => {
                          setFormData({
                            ...formData,
                            project: formData.project!.filter((id) => id !== projId),
                          });
                        }} />
                      </Tag>
                    </WrapItem>
                  ) : null;
                })}
              </Wrap>
            </Box>
          )}

          {/* Project Selection Dropdown */}
          <Select
            placeholder="Select project(s) to attach..."
            onChange={e => {
              if (e.target.value && !formData.project?.includes(e.target.value)) {
                setFormData({
                  ...formData,
                  project: [...(formData.project || []), e.target.value],
                });
              }
              e.target.value = '';
            }}
            size="lg"
            focusBorderColor="blue.400"
            borderWidth="2px"
          >
            {projects && projects.length > 0 &&
              projects
                .filter((proj: Project) => !formData.project?.includes(proj._id))
                .map((proj: Project) => (
                  <option key={proj._id} value={proj._id}>
                    {proj.projectName} ({proj.projectNumber})
                  </option>
                ))}
          </Select>
          <Text fontSize="xs" color="gray.500" mt={1}>
            You can select one or more projects to attach to this client
          </Text>
        </FormControl>
      </Box>

      <Divider />

      {/* Additional Information Toggle */}
      <Button
        onClick={handleToggleAdditional}
        leftIcon={showAdditional ? <ChevronUpIcon /> : <ChevronDownIcon />}
        size="md"
        alignSelf="flex-start"
        variant="outline"
        colorScheme="blue"
        fontWeight="semibold"
      >
        {showAdditional ? 'Hide Additional Information' : 'Show Additional Information'}
      </Button>

      {showAdditional && (
        <VStack spacing={6} align="stretch">
          {/* Contact Information Row */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={4}>
              Contact Information
            </Text>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="semibold">Lead Person Name</FormLabel>
                  <Input
                    value={formData.additionalInformation.leadPersonName}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        additionalInformation: {
                          ...formData.additionalInformation,
                          leadPersonName: e.target.value,
                        },
                      })
                    }
                    placeholder="Enter lead person name"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="semibold">Secondary Contact</FormLabel>
                  <Input
                    value={formData.additionalInformation.secondaryContact || ''}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        additionalInformation: {
                          ...formData.additionalInformation,
                          secondaryContact: e.target.value,
                        },
                      })
                    }
                    placeholder="Enter secondary contact (optional)"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mt={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="semibold">Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.additionalInformation.email}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        additionalInformation: {
                          ...formData.additionalInformation,
                          email: e.target.value,
                        },
                      })
                    }
                    placeholder="Enter email address"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="semibold">Phone</FormLabel>
                  <Input
                    value={formData.additionalInformation.phone}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        additionalInformation: {
                          ...formData.additionalInformation,
                          phone: e.target.value,
                        },
                      })
                    }
                    placeholder="Enter phone number"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: '1fr' }} gap={4} mt={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="semibold">Website URL</FormLabel>
                  <Input
                    type="url"
                    value={formData.additionalInformation.websiteUrl || ''}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        additionalInformation: {
                          ...formData.additionalInformation,
                          websiteUrl: e.target.value,
                        },
                      })
                    }
                    placeholder="https://example.com"
                    focusBorderColor="blue.400"
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </Box>
        </VStack>
      )}

      <Divider />

      {/* Action Buttons */}
      <HStack justify="flex-end" pt={4} spacing={4}>
        {onClose && (
          <Button 
            variant="ghost" 
            onClick={onClose} 
            isDisabled={isSubmitting}
            size="lg"
            minW="100px"
          >
            Cancel
          </Button>
        )}
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={isSubmitting}
          loadingText={isEdit ? 'Updating...' : 'Creating...'}
          size="lg"
          minW="120px"
          fontWeight="semibold"
        >
          {isEdit ? 'Update Client' : 'Create Client'}
        </Button>
      </HStack>
    </VStack>
  );
};

export default ClientForm;